import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  Image,
} from '@chakra-ui/react';
import { authService } from '../services/authService';
import { usePermissions } from '../hooks/usePermissions';

interface HeaderProps {
  title?: string;
  subtitle?: string;
}

export const Header: React.FC<HeaderProps> = ({ 
  title = "Sistema de Efetivação de Apólices", 
  subtitle = "Consulta de Veículos" 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { canAccessFleet, canAccessHome, canAccessFleetListingAdmin, canAccessBoletosAdmin } = usePermissions();
  
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const isActive = (path: string) => location.pathname === path;

  const handleNavigate = (path: string) => {
    console.log('Navegando para:', path);
    navigate(path, { replace: true });
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };
  
  // Função para fazer logout
  const handleLogout = () => {
    // Utilizar o serviço de autenticação para fazer logout
    authService.logout();
    
    // Redirecionar para a página de login
    navigate('/login');
  };

  return (
    <Box width="100%" position="sticky" top={0} zIndex={1000}>
      {/* Cabeçalho moderno */}
      <Box 
        bg="white"
        width="100%"
        position="relative"
        overflow="hidden"
        boxShadow="0 10px 30px rgba(0,0,0,0.15)"
      >
        {/* Barra superior com navegação */}
        <Flex 
          bg="linear-gradient(135deg, #3333CC 0%, #2828a3 100%)" 
          py={3} 
          px={6} 
          alignItems="center" 
          justifyContent="space-between"
          borderBottom="1px solid rgba(255,255,255,0.2)"
        >
          {/* Logo e Título */}
          <Flex alignItems="center" gap={4}>
            <Image 
              src="/banco-bs2-logo-branco-2.png" 
              alt="BS2 Seguros" 
              width="50px" 
              height="auto" 
            />
            <Text
              color="white"
              fontSize={{ base: "sm", md: "lg" }}
              fontWeight="600"
              letterSpacing="tight"
              display={{ base: "none", sm: "block" }}
            >
              {title}
            </Text>
          </Flex>
          
          {/* Menu desktop */}
          <Flex gap={6} display={{ base: "none", md: "flex" }} alignItems="center">
            {/* Item Trocar concessionária - só aparece quando um usuário master está impersonando um dealer */}
            {authService.isImpersonating() && (
              <Text
                color="white"
                fontWeight="normal"
                cursor="pointer"
                onClick={() => {
                  authService.returnToMaster();
                  handleNavigate('/master/select-dealer');
                }}
              >
                Trocar concessionária
              </Text>
            )}

            {/* Item Emissão de Apólice - só aparece se usuário tiver permissão */}
            {canAccessHome() && (
              <Text
                color="white"
                fontWeight={isActive('/home') ? "bold" : "normal"}
                cursor="pointer"
                position="relative"
                _after={
                  isActive('/home')
                    ? {
                        content: '""',
                        position: "absolute",
                        bottom: "-6px",
                        left: 0,
                        right: 0,
                        height: "3px",
                        bg: "white",
                        borderRadius: "3px",
                      }
                    : {}
                }
                onClick={() => {
                  console.log('Clicou em Emissão de Apólice');
                  navigate('/home', { replace: true });
                }}
                pointerEvents="auto"
                zIndex={10}
              >
                Emissão de Apólice
              </Text>
            )}
            
            {/* Item Cadastro de frota - só aparece se usuário tiver permissão */}
            {canAccessFleet() && (
              <Text
                color="white"
                fontWeight={isActive('/fleet') ? "bold" : "normal"}
                cursor="pointer"
                position="relative"
                _after={
                  isActive('/fleet')
                    ? {
                        content: '""',
                        position: "absolute",
                        bottom: "-6px",
                        left: 0,
                        right: 0,
                        height: "3px",
                        bg: "white",
                        borderRadius: "3px",
                      }
                    : {}
                }
                onClick={() => handleNavigate('/fleet')}
              >
                Cadastro de frota
              </Text>
            )}

            {/* Item Fleet Listing Admin - só aparece para Admin */}
            {canAccessFleetListingAdmin() && (
              <Text
                color="white"
                fontWeight={isActive('/admin/fleet-listing') ? "bold" : "normal"}
                cursor="pointer"
                position="relative"
                _after={
                  isActive('/admin/fleet-listing')
                    ? {
                        content: '""',
                        position: "absolute",
                        bottom: "-6px",
                        left: 0,
                        right: 0,
                        height: "3px",
                        bg: "white",
                        borderRadius: "3px",
                      }
                    : {}
                }
                onClick={() => handleNavigate('/admin/fleet-listing')}
              >
                Listagem de Frotas
              </Text>
            )}
            {canAccessBoletosAdmin() && (
              <Text
                color="white"
                fontWeight={isActive('/admin/boletos') ? "bold" : "normal"}
                cursor="pointer"
                position="relative"
                _after={
                  isActive('/admin/boletos')
                    ? {
                        content: '""',
                        position: "absolute",
                        bottom: "-6px",
                        left: 0,
                        right: 0,
                        height: "3px",
                        bg: "white",
                        borderRadius: "3px",
                      }
                    : {}
                }
                onClick={() => handleNavigate('/admin/boletos')}
              >
                Boletos
              </Text>
            )}
            
            {/* Botão de Logout */}
            <Button
              ml={4}
              size="md"
              bg="rgba(255,255,255,0.15)"
              backdropFilter="blur(10px)"
              borderWidth="2px"
              borderColor="rgba(255,255,255,0.3)"
              color="white"
              fontWeight="600"
              borderRadius="xl"
              _hover={{ 
                bg: "rgba(255,255,255,0.25)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
              }}
              transition="all 0.2s ease"
              onClick={handleLogout}
            >
              Sair
            </Button>
          </Flex>
          
          {/* Menu mobile */}
          <Box
            as="button"
            aria-label="Menu"
            color="white"
            display={{ base: "flex", md: "none" }}
            onClick={onOpen}
            _hover={{ bg: "rgba(255,255,255,0.1)" }}
            bg="transparent"
            border="none"
            p={2}
            borderRadius="md"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 18H21V16H3V18ZM3 13H21V11H3V13ZM3 6V8H21V6H3Z" fill="white"/>
            </svg>
          </Box>
        </Flex>
      </Box>

      {/* Menu mobile */}
      {isOpen && (
        <Box 
          position="fixed" 
          top={0} 
          right={0} 
          height="100vh" 
          width="250px" 
          bg="white" 
          zIndex={1000} 
          boxShadow="-4px 0 10px rgba(0,0,0,0.1)"
        >
          <Box 
            position="absolute" 
            top={4} 
            right={4} 
            cursor="pointer" 
            onClick={onClose}
            color="#3333CC"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
            </svg>
          </Box>
          
          <Box p={4} borderBottomWidth="1px" borderColor="gray.200">
            <Text fontSize="lg" fontWeight="bold" color="#3333CC">Menu</Text>
          </Box>
          
          <Flex direction="column" gap={4} mt={4} p={4}>
            {/* Item Trocar concessionária mobile - só aparece quando um usuário master está impersonando um dealer */}
            {authService.isImpersonating() && (
              <Text
                fontWeight="medium"
                color="gray.700"
                cursor="pointer"
                py={2}
                onClick={() => {
                  authService.returnToMaster();
                  handleNavigate('/master/select-dealer');
                  onClose();
                }}
              >
                Trocar concessionária
              </Text>
            )}

            {/* Item Emissão de Apólice mobile - só aparece se usuário tiver permissão */}
            {canAccessHome() && (
              <Text
                fontWeight={isActive('/home') ? "bold" : "medium"}
                color={isActive('/home') ? "#3333CC" : "gray.700"}
                cursor="pointer"
                py={2}
                onClick={() => {
                  console.log('Clicou em Emissão de Apólice (mobile)');
                  navigate('/home', { replace: true });
                  onClose();
                }}
                pointerEvents="auto"
              >
                Emissão de Apólice
              </Text>
            )}
            
            {/* Item Cadastro de frota mobile - só aparece se usuário tiver permissão */}
            {canAccessFleet() && (
              <Text
                fontWeight={isActive('/fleet') ? "bold" : "medium"}
                color={isActive('/fleet') ? "#3333CC" : "gray.700"}
                cursor="pointer"
                py={2}
                onClick={() => {
                  handleNavigate('/fleet');
                  onClose();
                }}
              >
                Cadastro de frota
              </Text>
            )}

            {/* Item Fleet Listing Admin mobile - só aparece para Admin */}
            {canAccessFleetListingAdmin() && (
              <Text
                fontWeight={isActive('/admin/fleet-listing') ? "bold" : "medium"}
                color={isActive('/admin/fleet-listing') ? "#3333CC" : "gray.700"}
                cursor="pointer"
                py={2}
                onClick={() => {
                  handleNavigate('/admin/fleet-listing');
                  onClose();
                }}
              >
                Fleet Listing
              </Text>
            )}
            {canAccessBoletosAdmin() && (
              <Text
                fontWeight={isActive('/admin/boletos') ? "bold" : "medium"}
                color={isActive('/admin/boletos') ? "#3333CC" : "gray.700"}
                cursor="pointer"
                py={2}
                onClick={() => {
                  handleNavigate('/admin/boletos');
                  onClose();
                }}
              >
                Boletos
              </Text>
            )}
            
            {/* Botão de Logout no menu mobile */}
            <Box mt={4} pt={4} borderTopWidth="1px" borderColor="gray.200">
              <Button
                colorScheme="blue"
                variant="outline"
                size="md"
                width="100%"
                onClick={() => {
                  handleLogout();
                  onClose();
                }}
              >
                Sair
              </Button>
            </Box>
          </Flex>
        </Box>
      )}
      
      {/* Overlay for mobile menu */}
      {isOpen && (
        <Box 
          position="fixed" 
          top={0} 
          left={0} 
          right={0} 
          bottom={0} 
          bg="blackAlpha.600" 
          zIndex={999} 
          onClick={onClose}
        />
      )}
    </Box>
  );
};
